import api from './api';

/**
 * Admin-Service: Slider & Slides (Startseiten-/Hero-Slider) – Flutter-Parität.
 *
 * Endpunkte (Admin-Routen: requiresAuth + onlyAdmin, Fehler als JSON-STRING!):
 *   GET    /api/sliders                        → Slider[] (direktes Array, ggf. aus Cache)
 *   GET    /api/sliders/locations/definitions  → { status, count, data: LocationDefinition[] }
 *   POST   /api/sliders                        → 201 { message, slider }
 *   PUT    /api/sliders/:id                    → { message, slider }
 *   DELETE /api/sliders/:id                    → { message }
 *   POST   /api/sliders/:id/toggle             → { message, slider }
 *   GET    /api/slides                         → Slide[] (ALLE, auch inaktive — für Admin!)
 *   GET    /api/slides/slider/:sliderId        → nur AKTIVE Slides (deshalb im Admin NICHT nutzen)
 *   POST   /api/slides                         → 201 { message, slide }
 *   PUT    /api/slides/:id                     → { message, slide }
 *   DELETE /api/slides/:id                     → { message }
 *   POST   /api/slides/:id/toggle              → { message, slide }
 *   GET    /api/sponsors                       → Sponsor[] (für Sponsor-Slides)
 *
 * WICHTIG: Slide-Bilder werden als KOMPLETTE Data-URL (data:image/...;base64,...)
 * im `image`-Feld mitgeschickt (Joi-Regex!) — NICHT über /api/file. Das Backend
 * speichert sie unter /uploads/slides/... und gibt diese URL zurück.
 */

export interface SliderLocationDefinition {
  location: string;
  name: string;
  description: string;
}

export interface AdminSlider {
  id: string;
  name: string;
  location: string;
  description: string | null;
  active: boolean;
  order: number;
  autoSlideDuration: number | null;
  createdAt: number | null;
  updatedAt: number | null;
}

export type SlideType = 'manual' | 'sponsor';

export interface AdminSlide {
  id: string;
  sliderId: string;
  type: SlideType;
  order: number;
  active: boolean;
  image: string | null;
  title: string | null;
  subtitle: string | null;
  linkUrl: string | null;
  buttonText: string | null;
  aspectRatio: string | null;
  sponsorIds: string[];
  createdAt: number | null;
  updatedAt: number | null;
}

export interface AdminSponsor {
  id: string;
  name: string | null;
  image: string | null;
  url: string | null;
  active: boolean;
}

export interface SlidePayload {
  order?: number | null;
  title?: string | null;
  subtitle?: string | null;
  linkUrl?: string | null;
  buttonText?: string | null;
  aspectRatio?: string | null;
  sponsorIds?: string[];
  /** Komplette Data-URL — NUR mitgeben, wenn ein neues Bild gewählt wurde
   *  (image im Payload überschreibt sonst die gespeicherte Bild-URL!). */
  image?: string;
}

const parseSlider = (d: any): AdminSlider | null => {
  if (!d || typeof d !== 'object') return null;
  return {
    id: d.id ?? d._id ?? '',
    name: d.name ?? '',
    location: d.location ?? '',
    description: d.description ?? null,
    active: d.active ?? true,
    order: typeof d.order === 'number' ? d.order : 0,
    autoSlideDuration: typeof d.autoSlideDuration === 'number' ? d.autoSlideDuration : null,
    createdAt: d.createdAt ?? null,
    updatedAt: d.updatedAt ?? null,
  };
};

const parseSlide = (d: any): AdminSlide | null => {
  if (!d || typeof d !== 'object') return null;
  return {
    id: d.id ?? d._id ?? '',
    sliderId: d.sliderId ?? '',
    type: d.type === 'sponsor' ? 'sponsor' : 'manual',
    order: typeof d.order === 'number' ? d.order : 0,
    active: d.active ?? true,
    image: d.image ?? null,
    title: d.title ?? null,
    subtitle: d.subtitle ?? null,
    linkUrl: d.linkUrl ?? null,
    buttonText: d.buttonText ?? null,
    aspectRatio: d.aspectRatio ?? null,
    sponsorIds: Array.isArray(d.sponsorIds) ? d.sponsorIds.filter((x: any) => typeof x === 'string') : [],
    createdAt: d.createdAt ?? null,
    updatedAt: d.updatedAt ?? null,
  };
};

const parseSponsor = (d: any): AdminSponsor | null => {
  if (!d || typeof d !== 'object') return null;
  return {
    id: d.id ?? d._id ?? '',
    name: d.name ?? null,
    image: d.image ?? null,
    url: d.url ?? null,
    active: d.active ?? true,
  };
};

const toArray = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

/** Leere Strings als null senden — Joi erlaubt zwar '', aber null hält die DB sauber. */
const emptyToNull = (v?: string | null): string | null => {
  const t = (v ?? '').trim();
  return t.length ? t : null;
};

class AdminSlidersService {
  // ---------------- Slider ----------------

  async listSliders(): Promise<AdminSlider[]> {
    const res = await api.get('/api/sliders');
    return toArray(res.data).map(parseSlider).filter(Boolean) as AdminSlider[];
  }

  async getLocationDefinitions(): Promise<SliderLocationDefinition[]> {
    const res = await api.get('/api/sliders/locations/definitions');
    return toArray(res.data).filter(
      (d: any) => d && typeof d.location === 'string'
    ) as SliderLocationDefinition[];
  }

  async createSlider(payload: {
    name: string;
    location: string;
    order?: number | null;
    autoSlideDuration?: number | null;
  }): Promise<AdminSlider | null> {
    const res = await api.post('/api/sliders', {
      name: payload.name.trim(),
      location: payload.location.trim(),
      order: payload.order ?? null,
      autoSlideDuration: payload.autoSlideDuration ?? null,
    });
    return parseSlider(res.data?.slider);
  }

  async updateSlider(
    id: string,
    payload: { name?: string; location?: string; order?: number | null; autoSlideDuration?: number | null }
  ): Promise<AdminSlider | null> {
    const res = await api.put(`/api/sliders/${id}`, payload);
    return parseSlider(res.data?.slider);
  }

  async deleteSlider(id: string): Promise<void> {
    await api.delete(`/api/sliders/${id}`);
  }

  async toggleSlider(id: string): Promise<AdminSlider | null> {
    const res = await api.post(`/api/sliders/${id}/toggle`);
    return parseSlider(res.data?.slider);
  }

  // ---------------- Slides ----------------

  /** ALLE Slides (auch inaktive) — die /slider/:id-Route liefert nur aktive! */
  async listAllSlides(): Promise<AdminSlide[]> {
    const res = await api.get('/api/slides');
    return toArray(res.data).map(parseSlide).filter(Boolean) as AdminSlide[];
  }

  async listSlidesForSlider(sliderId: string): Promise<AdminSlide[]> {
    const all = await this.listAllSlides();
    return all
      .filter((s) => s.sliderId === sliderId)
      .sort((a, b) => a.order - b.order);
  }

  async createSlide(
    sliderId: string,
    type: SlideType,
    payload: SlidePayload
  ): Promise<AdminSlide | null> {
    const body: any = {
      sliderId,
      type,
      order: payload.order ?? null,
      title: emptyToNull(payload.title),
      subtitle: emptyToNull(payload.subtitle),
      linkUrl: emptyToNull(payload.linkUrl),
      buttonText: emptyToNull(payload.buttonText),
      aspectRatio: emptyToNull(payload.aspectRatio),
      sponsorIds: payload.sponsorIds ?? [],
    };
    if (payload.image) body.image = payload.image;
    const res = await api.post('/api/slides', body);
    return parseSlide(res.data?.slide);
  }

  async updateSlide(id: string, payload: SlidePayload): Promise<AdminSlide | null> {
    const body: any = {
      order: payload.order ?? null,
      title: emptyToNull(payload.title),
      subtitle: emptyToNull(payload.subtitle),
      linkUrl: emptyToNull(payload.linkUrl),
      buttonText: emptyToNull(payload.buttonText),
      aspectRatio: emptyToNull(payload.aspectRatio),
    };
    if (payload.sponsorIds) body.sponsorIds = payload.sponsorIds;
    // image nur mitschicken, wenn ein NEUES Bild gewählt wurde — sonst würde
    // $set:{image:null} die gespeicherte Bild-URL löschen!
    if (payload.image) body.image = payload.image;
    const res = await api.put(`/api/slides/${id}`, body);
    return parseSlide(res.data?.slide);
  }

  async deleteSlide(id: string): Promise<void> {
    await api.delete(`/api/slides/${id}`);
  }

  async toggleSlide(id: string): Promise<AdminSlide | null> {
    const res = await api.post(`/api/slides/${id}/toggle`);
    return parseSlide(res.data?.slide);
  }

  // ---------------- Sponsoren ----------------

  async listSponsors(): Promise<AdminSponsor[]> {
    const res = await api.get('/api/sponsors');
    return toArray(res.data).map(parseSponsor).filter(Boolean) as AdminSponsor[];
  }

  // ---------------- Helfer ----------------

  /** Datei → komplette Data-URL (data:image/...;base64,...) für den Slide-Upload. */
  async fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.readAsDataURL(file);
    });
  }

  /** Bild-URL auflösen (/uploads/slides/... → Backend-URL; Data-URLs unverändert). */
  buildImageUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('data:')) return path;
    return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  /** Backend-Fehler sind hier JSON-STRINGS (res.status(500).json("Fehler...")). */
  getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { data?: any } };
    const data = anyErr?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      if (typeof data.error === 'string') return data.error;
      if (typeof data.message === 'string') return data.message;
    }
    return fallback;
  }
}

const adminSlidersService = new AdminSlidersService();
export default adminSlidersService;
